import { decodeWithTE2 } from "app/common/core/decode"
import { ArticleD, ArticleDC, ArticlePageD } from "app/data/articles/article.types"
import { ArticlePageVM, ArticleVM } from "app/models"
import { toCategoryVM } from "app/models/category.map"
import { LocalizedEntity, TranslationSelector } from "app/models/common"
import { toImageVXF } from "app/models/image.map"
import { LocaleD } from "db"
import { array, nonEmptyArray, option, taskEither } from "fp-ts"
import { flow, pipe } from "fp-ts/function"

type ArticleMap = LocalizedEntity<ArticleVM>

export const getPageVM = (articleV: ArticleVM, number: number): option.Option<ArticlePageVM> =>
  pipe(
    articleV.pages,
    array.findFirst((page) => page.number === number)
  )

const pageToArticlePageVM =
  (translationSelector: TranslationSelector, article: ArticleD, locale: LocaleD) =>
  (page: ArticlePageD): ArticlePageVM => ({
    article: {
      id: article.id,
      publishedAt: article.publishedAt,
      slug: pipe(article.translations, translationSelector, (translation) => translation.slug),
      summary: pipe(
        article.pages,
        nonEmptyArray.head,
        (page) => page.translations,
        translationSelector,
        (translation) => translation.summary
      ),
      title: pipe(
        article.pages,
        nonEmptyArray.head,
        (page) => page.translations,
        translationSelector,
        (translation) => translation.title
      ),
      updatedAt: article.updatedAt,
    },
    content: translationSelector(page.translations).content,
    id: page.id,
    leadImage: toImageVXF(translationSelector)(page.leadImage),
    locale,
    number: page.number,
    summary: translationSelector(page.translations).summary,
    title: translationSelector(page.translations).title,
  })

export const mapToArticleVM =
  (translationSelector: TranslationSelector, locale: LocaleD) =>
  (article: ArticleD): ArticleVM =>
    pipe(
      {
        article,
        leadPage: nonEmptyArray.head(article.pages),
      },
      ({ article, leadPage }) => ({
        authors: article.authors,
        categories: pipe(
          article.categories,
          array.map((c) => pipe(c.translations, translationSelector, toCategoryVM))
        ),
        id: article.id,
        image: pipe(leadPage, (page) => page.leadImage, toImageVXF(translationSelector)),
        locale,
        pages: pipe(
          article.pages,
          nonEmptyArray.map(pageToArticlePageVM(translationSelector, article, locale))
        ),
        publishedAt: article.publishedAt,
        slug: pipe(article.translations, translationSelector, (translation) => translation.slug),
        summary: pipe(
          leadPage,
          (leadPage) => leadPage.translations,
          translationSelector,
          (translation) => translation.summary
        ),
        title: pipe(
          leadPage,
          (leadPage) => leadPage.translations,
          translationSelector,
          (translation) => translation.title
        ),
        updatedAt: article.updatedAt,
      })
    )

export const toArticleMap = (article: ArticleD): ArticleMap => ({
  en_ca: mapToArticleVM(nonEmptyArray.head, LocaleD.en_ca)(article),
  fr_ca: mapToArticleVM(nonEmptyArray.last, LocaleD.fr_ca)(article),
})

export const decodeToArticleMap = flow(decodeWithTE2(ArticleDC), taskEither.map(toArticleMap))

export const decodeToArticle = (locale: LocaleD) =>
  flow(
    decodeToArticleMap,
    taskEither.map((map) => map[locale])
  )
